import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as BarTooltip,
  Label,
} from 'recharts';

import styles, { rechartsStyles, PURPLE, PAIR_COLOR } from './MeterDetailsConsumptionChart.styles';
import { makeDefaultDateRange21 } from '../../utils/dates';
import { useEnergySummary } from '../../hooks/useEnergySummary';

import {
  labelFormatterFromPayload,
  formatTooltipSingle,
  formatTooltipBoth,
} from '../../utils/meterDetails.helpers';

import {
  computeEffectiveDateRange,
  pickBaseSeries,
  parseDatesFromSeries,
  makeChartData,
  computeMonthYearLabel,
  buildYAxisDynamic,
  computeYAxisWidth,
} from '../../utils/meterDetailsChart.helpers';

export function MeterDetailsConsumptionLegend({ meterType }) {
  const entries =
    meterType === 3
      ? [
          { label: 'AC Energy', color: PURPLE },
          { label: 'DC Energy', color: PAIR_COLOR },
        ]
      : [{ label: meterType === 1 ? 'AC Energy' : 'DC Energy', color: PURPLE }];

  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
      {entries.map((e) => (
        <Box key={e.label} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 4, backgroundColor: e.color }} />
          <span style={{ fontSize: 11, opacity: 0.85 }}>{e.label}</span>
        </Box>
      ))}
    </Box>
  );
}

export default function MeterDetailsConsumptionChart({
  meterNumber,
  startDate,
  endDate,
  meterType,
  data = [],
  loading = false,
  dateRange,
  evn,
}) {
  const [localRange] = useState(() => makeDefaultDateRange21());

  const effectiveDateRange = useMemo(
    () => computeEffectiveDateRange(dateRange, startDate, endDate, localRange),
    [dateRange, startDate, endDate, localRange]
  );

  const { data: fetchedData, loading: fetching } = useEnergySummary({
    meterNumber,
    meterType,
    range: effectiveDateRange,
    evn,
  });

  const baseSeries = useMemo(
    () => pickBaseSeries(fetchedData, data, effectiveDateRange, meterType),
    [fetchedData, data, effectiveDateRange, meterType]
  );

  const parsedDates = useMemo(() => parseDatesFromSeries(baseSeries), [baseSeries]);

  const monthYearLabel = useMemo(
    () => computeMonthYearLabel(parsedDates, effectiveDateRange),
    [parsedDates, effectiveDateRange]
  );

  const chartData = useMemo(() => makeChartData(baseSeries, meterType), [baseSeries, meterType]);
  const manyPoints = chartData.length > 16;

  const { yMax, ticks } = useMemo(() => {
    const vals =
      meterType === 3
        ? chartData.flatMap((d) => [d.ac ?? 0, d.dc ?? 0])
        : chartData.map((d) => d.value ?? 0);
    return buildYAxisDynamic(vals, 250, 2000);
  }, [chartData, meterType]);

  const yAxisWidth = useMemo(() => computeYAxisWidth(ticks), [ticks]);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.chartWrapper}>
        {(loading || fetching) && (
          <Box sx={styles.loadingOverlay}>
            <span style={{ fontSize: '0.88rem', opacity: 0.8 }}>Loading…</span>
          </Box>
        )}

        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={rechartsStyles.margin} barCategoryGap="18%" maxBarSize={64}>
            <XAxis
              dataKey="day"
              interval={0}
              minTickGap={4}
              tickMargin={10}
              height={manyPoints ? 28 : 22}
              tick={{ fontSize: 10, dy: 6 }}
            >
              <Label
                value={monthYearLabel}
                position="insideBottom"
                dy={24}
                style={{ textAnchor: 'middle', fontSize: 11, opacity: 0.75 }}
              />
            </XAxis>

            <YAxis domain={[0, yMax]} ticks={ticks} tick={{ fontSize: 10 }} width={yAxisWidth}>
              <Label
                value="kWh"
                angle={-90}
                position="insideLeft"
                offset={2}
                style={{ textAnchor: 'middle', fontSize: 10, opacity: 0.75 }}
              />
            </YAxis>

            <BarTooltip
              labelFormatter={labelFormatterFromPayload}
              formatter={meterType === 3 ? formatTooltipBoth : formatTooltipSingle}
              wrapperStyle={rechartsStyles.tooltip}
            />

            {meterType === 3 ? (
              <>
                <Bar dataKey="ac" name="AC Energy" fill={PURPLE} />
                <Bar dataKey="dc" name="DC Energy" fill={PAIR_COLOR} />
              </>
            ) : (
              <Bar dataKey="value" name={meterType === 1 ? 'AC Energy' : 'DC Energy'} fill={PURPLE} />
            )}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
